import { ChecklistItem, ProjectNode, ViewState } from '@/shared/types/projectData';
import {
  buildKanbanViewState,
  getCoreKanbanColumnIdForStatus,
  getKanbanCompletionState,
  isCoreKanbanColumnId,
  normalizeKanbanColumnId,
  normalizeKanbanStatus,
} from '@/shared/utils/kanbanState';

type TaskStateNode = Partial<ProjectNode> & {
  status?: ProjectNode['status'];
  checklist?: ChecklistItem[];
};

const isDateOnlyValue = (value?: string | null): boolean =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const inferCalendarAllDay = (
  task: Pick<TaskStateNode, 'type' | 'startDate' | 'endDate'>
): boolean | undefined => {
  if (task.type !== 'task') {
    return undefined;
  }

  const startValue = task.startDate || task.endDate;
  const endValue = task.endDate || task.startDate;
  if (!startValue && !endValue) {
    return undefined;
  }

  return isDateOnlyValue(startValue) && isDateOnlyValue(endValue);
};

const mergeCalendarAllDay = (
  updates: TaskStateNode,
  currentTask: TaskStateNode,
  force = false
): TaskStateNode => {
  const mergedCalendar = {
    ...currentTask.viewState?.calendar,
    ...updates.viewState?.calendar,
  };
  const mergedViewState: ViewState = {
    ...currentTask.viewState,
    ...updates.viewState,
    ...(mergedCalendar.allDay === undefined
      ? {}
      : { calendar: { ...mergedCalendar, allDay: mergedCalendar.allDay } }),
  };
  const mergedTask = {
    ...currentTask,
    ...updates,
    viewState: mergedViewState,
  };

  const inferredAllDay = inferCalendarAllDay(mergedTask);
  if (inferredAllDay === undefined) {
    return updates;
  }

  if (!force && updates.startDate === undefined && updates.endDate === undefined && updates.date === undefined) {
    return updates;
  }

  return {
    ...updates,
    viewState: {
      ...mergedTask.viewState,
      calendar: {
        ...mergedTask.viewState?.calendar,
        allDay: inferredAllDay,
      },
    },
  };
};

const calculateChecklistProgress = (checklist?: ChecklistItem[]): number => {
  if (!checklist || checklist.length === 0) return 0;

  const completedItems = checklist.filter((item) => item.checked).length;
  return Number(((completedItems / checklist.length) * 100).toFixed(2));
};

const isSpecialCompletionState = (
  state: TaskStateNode['completionState']
): state is 'blocked' | 'cancelled' => state === 'blocked' || state === 'cancelled';

const resolveSpecialCompletionState = (
  updates: TaskStateNode,
  currentTask: TaskStateNode
): 'blocked' | 'cancelled' | undefined => {
  // Reloads and editors also send full snapshots. Presence alone is not intent.
  if (updates.completionState !== undefined && updates.completionState !== currentTask.completionState) {
    return isSpecialCompletionState(updates.completionState) ? updates.completionState : undefined;
  }
  if (!isSpecialCompletionState(currentTask.completionState)) return undefined;

  const progress = currentTask.checklist?.length
    ? calculateChecklistProgress(currentTask.checklist) : (currentTask.progress ?? 0);
  const specialStatus = progress <= 0 ? 'todo' : 'in-progress';
  const echo = updates.completionState === currentTask.completionState;
  const currentStatus = normalizeKanbanStatus(currentTask.status || currentTask.completionState);
  const currentColumn = normalizeKanbanColumnId(currentTask.viewState?.kanban?.columnId);
  // Repairing a legacy Done projection is an echo when the special lifecycle
  // remains selected. It must not be interpreted as a resume action.
  const changedStatus = updates.status !== undefined &&
    !(echo ? [currentStatus, specialStatus] : [currentStatus]).includes(normalizeKanbanStatus(updates.status));
  const column = updates.viewState?.kanban?.columnId;
  const changedCoreColumn = !!column && isCoreKanbanColumnId(column) &&
    normalizeKanbanColumnId(column) !== currentColumn &&
    !(echo && isCoreKanbanColumnId(currentColumn) && normalizeKanbanColumnId(column) === specialStatus);
  return changedStatus || changedCoreColumn ? undefined : currentTask.completionState;
};

export const synchronizeTaskState = (
  updates: TaskStateNode,
  triggerField: string,
  currentTask: TaskStateNode
): TaskStateNode => {
  const mergedTask = { ...currentTask, ...updates };
  const currentColumnId = normalizeKanbanColumnId(mergedTask.viewState?.kanban?.columnId);
  const currentStatus = normalizeKanbanStatus(currentTask.status || currentTask.completionState);
  const checklist = mergedTask.checklist || [];
  const checklistProgress = calculateChecklistProgress(checklist);
  const finalUpdates: TaskStateNode = { ...updates };
  const specialCompletionState = resolveSpecialCompletionState(updates, currentTask);

  const resolveTargetColumnId = (targetStatus: 'todo' | 'in-progress' | 'done') => {
    if (!isCoreKanbanColumnId(currentColumnId) && currentStatus === targetStatus) {
      return currentColumnId;
    }
    return getCoreKanbanColumnIdForStatus(targetStatus);
  };

  // Checklist evidence is authoritative. Lifecycle edits never check boxes.
  let progress = mergedTask.progress || 0;
  if (checklist.length > 0) {
    progress = checklistProgress;
    finalUpdates.checklist = checklist;
  } else if (triggerField === 'status' && !specialCompletionState) {
    const status = normalizeKanbanStatus(updates.status || mergedTask.status || mergedTask.completionState);
    progress = status === 'todo' ? 0 : status === 'done' ? 100 : (progress > 0 && progress < 100 ? progress : 50);
  } else if (triggerField === 'progress') {
    progress = Math.max(0, Math.min(100, updates.progress ?? progress));
  }
  // Removing the last checklist item keeps the last percentage as manual progress.
  const reviewFields = mergedTask.customFields ?? {};
  const reviewGate = String(reviewFields.requiredQualityGate || reviewFields.required_quality_gate || '').trim();
  const reviewStatus = String(reviewFields.reviewStatus || reviewFields.review_status || '').trim().toLowerCase();
  const decision = reviewFields.qualityDecision || reviewFields.quality_decision;
  const decisionStatus = decision && typeof decision === 'object' && 'status' in decision
    ? String(decision.status || '').trim().toLowerCase() : '';
  const waitingForReview = reviewGate === 'independent_review' && reviewStatus !== 'approved' && decisionStatus !== 'approved';
  const status = progress <= 0 ? 'todo' : progress >= 100 && !waitingForReview && !specialCompletionState ? 'done' : 'in-progress';
  finalUpdates.progress = progress;
  finalUpdates.status = status;
  finalUpdates.completionState = specialCompletionState || getKanbanCompletionState(status);
  finalUpdates.viewState = buildKanbanViewState(
    mergedTask.viewState ?? {}, resolveTargetColumnId(status), mergedTask.viewState?.kanban?.order || 0,
  );

  finalUpdates.updatedAt = new Date().toISOString();
  return finalUpdates;
};

export const normalizeTaskStateUpdate = (
  updates: TaskStateNode,
  currentTask?: TaskStateNode | null
): TaskStateNode => {
  if (!currentTask || currentTask.type !== 'task') {
    return updates;
  }

  const isSnapshotLikeUpdate =
    updates.id !== undefined ||
    updates.type !== undefined ||
    updates.title !== undefined ||
    (updates as TaskStateNode & Record<string, unknown>).createdAt !== undefined ||
    (updates as TaskStateNode & Record<string, unknown>).updatedAt !== undefined;
  const updatesWithCalendar = mergeCalendarAllDay(updates, currentTask, isSnapshotLikeUpdate);

  if ((updatesWithCalendar.checklist ?? currentTask.checklist)?.length ||
      resolveSpecialCompletionState(updatesWithCalendar, currentTask)) {
    const next = synchronizeTaskState(
      updatesWithCalendar, updatesWithCalendar.progress !== undefined ? 'progress' : 'checklist', currentTask,
    );
    const column = updatesWithCalendar.viewState?.kanban?.columnId;
    if (column && !isCoreKanbanColumnId(normalizeKanbanColumnId(column))) {
      next.viewState = buildKanbanViewState(next.viewState || {}, column, updatesWithCalendar.viewState?.kanban?.order ?? 0);
    }
    return next;
  }

  const explicitColumnId = updatesWithCalendar.viewState?.kanban?.columnId
    ? normalizeKanbanColumnId(updatesWithCalendar.viewState.kanban.columnId)
    : undefined;
  const explicitOrder = updatesWithCalendar.viewState?.kanban?.order ?? currentTask.viewState?.kanban?.order ?? 0;
  const withExplicitColumn = (nextUpdates: TaskStateNode) => {
    if (!explicitColumnId) {
      return nextUpdates;
    }

    return {
      ...nextUpdates,
      viewState: buildKanbanViewState(
        nextUpdates.viewState || currentTask.viewState || {},
        isCoreKanbanColumnId(explicitColumnId) && nextUpdates.status
          ? getCoreKanbanColumnIdForStatus(normalizeKanbanStatus(nextUpdates.status)) : explicitColumnId,
        explicitOrder,
      ),
    };
  };

  if (explicitColumnId && isCoreKanbanColumnId(explicitColumnId) && isSnapshotLikeUpdate) {
    const columnStatus = normalizeKanbanStatus(explicitColumnId);
    const snapshotStatus = normalizeKanbanStatus(
      updatesWithCalendar.status ||
      (updatesWithCalendar as TaskStateNode & Record<string, unknown>).completionState as string ||
      currentTask.status ||
      currentTask.completionState
    );

    if (snapshotStatus !== columnStatus) {
      return withExplicitColumn(
        synchronizeTaskState(
          {
            ...updatesWithCalendar,
            status: columnStatus,
          },
          'status',
          currentTask
        )
      );
    }
  }

  if (updatesWithCalendar.progress !== undefined) {
    return withExplicitColumn(synchronizeTaskState(updatesWithCalendar, 'progress', currentTask));
  }

  if (updatesWithCalendar.status !== undefined) {
    return withExplicitColumn(synchronizeTaskState(updatesWithCalendar, 'status', currentTask));
  }

  if (updatesWithCalendar.completionState !== undefined) {
    const derivedStatus =
      updatesWithCalendar.completionState === 'completed'
        ? 'done'
        : updatesWithCalendar.completionState === 'in-progress'
        ? 'in-progress'
        : 'todo';

    return withExplicitColumn(
      synchronizeTaskState(
        {
          ...updatesWithCalendar,
          status: derivedStatus,
        },
        'status',
        currentTask
      )
    );
  }

  if (updatesWithCalendar.checklist !== undefined) {
    return withExplicitColumn(synchronizeTaskState(updatesWithCalendar, 'checklist', currentTask));
  }

  if (explicitColumnId) {
    if (isCoreKanbanColumnId(explicitColumnId)) {
      return withExplicitColumn(
        synchronizeTaskState(
          {
            ...updatesWithCalendar,
            status: normalizeKanbanStatus(explicitColumnId),
          },
          'status',
          currentTask
        )
      );
    }

    return withExplicitColumn(updatesWithCalendar);
  }

  return updatesWithCalendar;
};
